import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface ClinicSettings {
  id: string
  clinic_name: string
  address: string
  phone: string
  license_number: string
  weight_unit: 'lbs' | 'kg'
  temperature_unit: 'F' | 'C'
}

export interface Veterinarian {
  id: string
  name: string
  license_number: string
  specialties: string
  created_at: string
  updated_at: string
}

interface SettingsState {
  clinic: ClinicSettings | null
  veterinarians: Veterinarian[]
  loading: boolean
  vetsLoading: boolean
  error: string | null
}

const initialState: SettingsState = {
  clinic: null,
  veterinarians: [],
  loading: false,
  vetsLoading: false,
  error: null,
}

export const fetchClinicSettings = createAsyncThunk('settings/fetchClinic', async () => {
  const res = await fetch('/.netlify/functions/settings')
  if (!res.ok) throw new Error('Failed to fetch clinic settings')
  return res.json() as Promise<ClinicSettings>
})

export const updateClinicSettings = createAsyncThunk(
  'settings/updateClinic',
  async (data: Partial<ClinicSettings>) => {
    const res = await fetch('/.netlify/functions/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update clinic settings')
    return res.json() as Promise<ClinicSettings>
  }
)

export const fetchVeterinarians = createAsyncThunk('settings/fetchVets', async () => {
  const res = await fetch('/.netlify/functions/veterinarians')
  if (!res.ok) throw new Error('Failed to fetch veterinarians')
  return res.json() as Promise<Veterinarian[]>
})

export const addVeterinarian = createAsyncThunk(
  'settings/addVet',
  async (data: { name: string; license_number: string; specialties: string }) => {
    const res = await fetch('/.netlify/functions/veterinarians', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to add veterinarian')
    return res.json() as Promise<Veterinarian>
  }
)

export const updateVeterinarian = createAsyncThunk(
  'settings/updateVet',
  async ({ id, ...data }: { id: string; name: string; license_number: string; specialties: string }) => {
    const res = await fetch(`/.netlify/functions/veterinarians/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update veterinarian')
    return res.json() as Promise<Veterinarian>
  }
)

export const deleteVeterinarian = createAsyncThunk(
  'settings/deleteVet',
  async (id: string) => {
    const res = await fetch(`/.netlify/functions/veterinarians/${id}`, {
      method: 'DELETE',
    })
    if (!res.ok) throw new Error('Failed to delete veterinarian')
    return id
  }
)

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchClinicSettings.pending, (state) => {
        if (!state.clinic) state.loading = true
      })
      .addCase(fetchClinicSettings.fulfilled, (state, action) => {
        state.clinic = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchClinicSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load settings'
      })
      .addCase(updateClinicSettings.fulfilled, (state, action) => {
        state.clinic = action.payload
      })
      .addCase(fetchVeterinarians.pending, (state) => {
        if (state.veterinarians.length === 0) state.vetsLoading = true
      })
      .addCase(fetchVeterinarians.fulfilled, (state, action) => {
        state.veterinarians = action.payload
        state.vetsLoading = false
      })
      .addCase(fetchVeterinarians.rejected, (state) => {
        state.vetsLoading = false
      })
      .addCase(addVeterinarian.fulfilled, (state, action) => {
        state.veterinarians.push(action.payload)
        state.veterinarians.sort((a, b) => a.name.localeCompare(b.name))
      })
      .addCase(updateVeterinarian.fulfilled, (state, action) => {
        const idx = state.veterinarians.findIndex((v) => v.id === action.payload.id)
        if (idx !== -1) state.veterinarians[idx] = action.payload
      })
      .addCase(deleteVeterinarian.fulfilled, (state, action) => {
        state.veterinarians = state.veterinarians.filter((v) => v.id !== action.payload)
      })
  },
})

export default settingsSlice.reducer
